import { Calendar, Users, GitBranch, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FilterOptions, PeriodFilter } from "./dashboardUtils";
import type { DealPipeline, Sale } from "../types";

interface DashboardHeaderProps {
  filters: FilterOptions;
  onFilterChange: (newFilters: FilterOptions) => void;
  pipelines: DealPipeline[];
  salesTeam: Sale[];
  onRefresh: () => void;
  isRefreshing?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  filters,
  onFilterChange,
  pipelines,
  salesTeam,
  onRefresh,
  isRefreshing,
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-border/60">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <span>Dashboard Geral</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Visão unificada em tempo real de funis de vendas, metas, desempenho da
          equipe e atividades.
        </p>
      </div>

      {/* Controles de Filtros */}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Filtro de Período */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-2.5 py-1 shadow-xs">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.period}
            onValueChange={(val) =>
              onFilterChange({ ...filters, period: val as PeriodFilter })
            }
          >
            <SelectTrigger className="h-7 border-0 bg-transparent text-xs p-0 focus:ring-0 focus:ring-offset-0 w-32 shadow-none font-medium">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo o Período</SelectItem>
              <SelectItem value="this_month">Este Mês</SelectItem>
              <SelectItem value="last_30_days">Últimos 30 Dias</SelectItem>
              <SelectItem value="this_quarter">Este Trimestre</SelectItem>
              <SelectItem value="this_year">Este Ano</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filtro por Funil */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-2.5 py-1 shadow-xs">
          <GitBranch className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.pipelineId}
            onValueChange={(val) =>
              onFilterChange({ ...filters, pipelineId: val })
            }
          >
            <SelectTrigger className="h-7 border-0 bg-transparent text-xs p-0 focus:ring-0 focus:ring-offset-0 w-36 shadow-none font-medium truncate">
              <SelectValue placeholder="Todos os Funis" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Funis</SelectItem>
              {pipelines.map((pipe) => (
                <SelectItem key={pipe.id} value={String(pipe.id)}>
                  {pipe.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtro por Vendedor */}
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-2.5 py-1 shadow-xs">
          <Users className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.salesId}
            onValueChange={(val) =>
              onFilterChange({ ...filters, salesId: val })
            }
          >
            <SelectTrigger className="h-7 border-0 bg-transparent text-xs p-0 focus:ring-0 focus:ring-offset-0 w-36 shadow-none font-medium truncate">
              <SelectValue placeholder="Toda a Equipe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda a Equipe</SelectItem>
              {salesTeam.map((member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Botão de Atualizar */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-9 px-2.5 gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          title="Recarregar dados do dashboard"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-primary" : ""}`}
          />
          <span className="hidden sm:inline">Atualizar</span>
        </Button>
      </div>
    </div>
  );
};
